import React, { useEffect, useState, useCallback, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './GuidedTour.css';

/**
 * GuidedTour — tour guiado estilo product walkthrough (Hikoot/Intro.js).
 *
 * Overlay escuro + "spotlight" no elemento alvo + tooltip posicionado
 * logo ao lado, com prev/next e dots de progresso.
 *
 * Por que um componente próprio em vez de Intro.js/Shepherd:
 *   - Zero dependência externa (bundle size)
 *   - Dark mode nativo via var(--*)
 *   - SVG mask pro "hole" (mais consistente que box-shadow trick)
 *   - Portal pra escapar de contextos com overflow:hidden
 *
 * API:
 *
 *   <GuidedTour
 *     tourId="services"                  // localStorage key
 *     steps={[
 *       {
 *         selector: '[data-tour="new-btn"]',  // CSS selector do alvo
 *         title: 'Titulo curto',
 *         content: 'Explicacao de 1-2 frases.',
 *         placement: 'bottom',                 // top|bottom|left|right
 *       },
 *       // Passo sem selector = tooltip centralizado (intro ou outro)
 *       { title: 'Dica final', content: '...' },
 *     ]}
 *     autoStart                           // abre automaticamente na 1a visita
 *   />
 *
 * Ref pra controle externo (abrir manualmente):
 *
 *   const ref = useRef();
 *   <GuidedTour ref={ref} ... />
 *   <button onClick={() => ref.current.open()}>Ver tutorial</button>
 *
 * Defensivo:
 *   - Selectors que não resolvem → passo é pulado silenciosamente
 *   - ESC fecha; setas navegam
 *   - Scroll/resize durante tour recalculam posição
 */

const STORAGE_PREFIX = 'atendo_tour_seen:';

const GuidedTour = React.forwardRef(function GuidedTour(
  { tourId, steps = [], autoStart = true, onComplete },
  ref
) {
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const storageKey = `${STORAGE_PREFIX}${tourId}`;

  React.useImperativeHandle(ref, () => ({
    open: () => { setStepIndex(0); setActive(true); },
    close: () => setActive(false),
    reset: () => { try { localStorage.removeItem(storageKey); } catch {} },
  }), [storageKey]);

  // Auto-start na primeira visita (delay pra page render terminar)
  useEffect(() => {
    if (!autoStart) return;
    let seen = false;
    try { seen = localStorage.getItem(storageKey) === '1'; } catch {}
    if (seen) return;
    const t = setTimeout(() => setActive(true), 700);
    return () => clearTimeout(t);
  }, [autoStart, storageKey]);

  const recalcRect = useCallback(() => {
    if (!active) return;
    const step = steps[stepIndex];
    if (!step || !step.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
  }, [active, stepIndex, steps]);

  // Scroll do target into view + recalcula posição
  useLayoutEffect(() => {
    if (!active) return;
    const step = steps[stepIndex];
    if (step?.selector) {
      const el = document.querySelector(step.selector);
      if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'center' });
    }
    const t1 = setTimeout(recalcRect, 50);
    const t2 = setTimeout(recalcRect, 450); // 2a passada após smooth scroll
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [active, stepIndex, recalcRect, steps]);

  // Resize/scroll durante tour → recalcula
  useEffect(() => {
    if (!active) return;
    const handler = () => recalcRect();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [active, recalcRect]);

  const finish = useCallback((completed) => {
    try { localStorage.setItem(storageKey, '1'); } catch {}
    setActive(false);
    setStepIndex(0);
    if (onComplete) onComplete(completed);
  }, [storageKey, onComplete]);

  const next = useCallback(() => {
    if (stepIndex < steps.length - 1) setStepIndex(i => i + 1);
    else finish(true);
  }, [stepIndex, steps.length, finish]);

  const prev = useCallback(() => {
    if (stepIndex > 0) setStepIndex(i => i - 1);
  }, [stepIndex]);

  // Acessibilidade: ESC fecha, setas navegam
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); finish(false); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, next, prev, finish]);

  if (!active || steps.length === 0) return null;

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;
  const placement = step.placement || 'bottom';
  const tooltipPos = rect ? computeTooltipPosition(rect, placement) : centerPosition();

  return createPortal(
    <div className="tour-root" role="dialog" aria-modal="true" aria-label={step.title || 'Tutorial'}>
      {rect ? (
        <SvgSpotlight rect={rect} onClick={() => finish(false)} />
      ) : (
        <div className="tour-overlay-full" onClick={() => finish(false)} />
      )}

      <div
        className={`tour-tooltip tour-tooltip-${placement}${!rect ? ' tour-tooltip-centered' : ''}`}
        style={tooltipPos}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-dots" aria-hidden="true">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`tour-dot${i === stepIndex ? ' active' : ''}${i < stepIndex ? ' done' : ''}`}
            />
          ))}
        </div>

        <button className="tour-close" onClick={() => finish(false)} aria-label="Fechar tutorial">×</button>

        {step.title && <h3 className="tour-title">{step.title}</h3>}
        {step.content && <p className="tour-content">{step.content}</p>}

        <div className="tour-footer">
          <span className="tour-step-count">{stepIndex + 1} de {steps.length}</span>
          <div className="tour-buttons">
            {!isFirst && (
              <button type="button" className="tour-btn tour-btn-ghost" onClick={prev}>← Voltar</button>
            )}
            <button type="button" className="tour-btn tour-btn-primary" onClick={next}>
              {isLast ? 'Concluir' : 'Próximo →'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
});

// SVG mask com "hole" no elemento alvo — overlay escuro semi-transparente
// cobre a tela inteira exceto o retângulo do target (com borda azul).
function SvgSpotlight({ rect, onClick }) {
  const pad = 8, r = 10;
  const x = Math.max(0, rect.left - pad);
  const y = Math.max(0, rect.top - pad);
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;
  return (
    <svg className="tour-spotlight" onClick={onClick} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <mask id="tour-hole">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="black" />
        </mask>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill="rgba(15,23,42,0.55)" mask="url(#tour-hole)" />
      <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke="rgba(37,99,235,0.9)" strokeWidth="2" />
    </svg>
  );
}

function computeTooltipPosition(rect, placement) {
  const margin = 16, tipW = 340, tipH = 180;
  const vw = window.innerWidth, vh = window.innerHeight;
  let top, left;
  switch (placement) {
    case 'top':
      top = rect.top - tipH - margin;
      left = rect.left + rect.width / 2 - tipW / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tipH / 2;
      left = rect.left - tipW - margin;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tipH / 2;
      left = rect.right + margin;
      break;
    default:
      top = rect.bottom + margin;
      left = rect.left + rect.width / 2 - tipW / 2;
  }
  const pad = 12;
  top = Math.max(pad, Math.min(vh - tipH - pad, top));
  left = Math.max(pad, Math.min(vw - tipW - pad, left));
  return { top, left };
}

function centerPosition() {
  return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
}

export default GuidedTour;
